import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FileSpreadsheet } from "lucide-react";
import * as ownerApi from "../../api/payroll/payrollReportApi";
import * as portalApi from "../../api/payroll/payrollReportPortalApi";
import { Card, CardContent, Badge, Alert, Table, EmptyState, Spinner } from "../../components/ui";
import { useToast } from "../../context/ToastContext";

export default function PayrollRegister({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const { runId } = useParams();
  const base = isPortal ? "/staff-panel/payroll-runs" : "/payroll-runs";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getPayrollRegister(runId);
        setData(res?.data || res);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load register");
      } finally {
        setLoading(false);
      }
    })();
  }, [runId, isPortal, api]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2 text-neutral-500 dark:text-neutral-400">
        <Spinner size="lg" label="Loading payroll register…" />
        <span className="text-xs font-semibold">Loading payroll register...</span>
      </div>
    );
  }

  const rows = data?.register || [];
  const run = data?.run || {};

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const tableRows = rows.map((r, i) => ({ ...r, id: r.staffCode || i }));

  const columns = [
    {
      key: "staffCode",
      header: "Code",
      render: (r) => (
        <Badge variant="default" className="font-mono">
          {r.staffCode || "—"}
        </Badge>
      ),
    },
    {
      key: "name",
      header: "Name",
      nowrap: true,
      render: (r) => <span className="font-semibold text-neutral-900 dark:text-neutral-100">{r.name}</span>,
    },
    {
      key: "grossPay",
      header: "Gross",
      align: "right",
      render: (r) => `₹${Number(r.grossPay || 0).toLocaleString()}`,
    },
    {
      key: "totalDeductions",
      header: "Deductions",
      align: "right",
      render: (r) => (
        <span className="text-danger-700">₹{Number(r.totalDeductions || 0).toLocaleString()}</span>
      ),
    },
    {
      key: "lopDays",
      header: "LOP",
      align: "right",
      render: (r) => <span className="font-semibold text-warning-700">{r.lopDays || 0}</span>,
    },
    {
      key: "overtimePay",
      header: "OT",
      align: "right",
      render: (r) => (
        <span className="text-success-700">₹{Number(r.overtimePay || 0).toLocaleString()}</span>
      ),
    },
    {
      key: "pfEmployee",
      header: "PF",
      align: "right",
      render: (r) => `₹${Number(r.pfEmployee || 0).toLocaleString()}`,
    },
    {
      key: "esiEmployee",
      header: "ESI",
      align: "right",
      render: (r) => `₹${Number(r.esiEmployee || 0).toLocaleString()}`,
    },
    {
      key: "professionalTax",
      header: "PT",
      align: "right",
      render: (r) => `₹${Number(r.professionalTax || 0).toLocaleString()}`,
    },
    {
      key: "tds",
      header: "TDS",
      align: "right",
      render: (r) => `₹${Number(r.tds || 0).toLocaleString()}`,
    },
    {
      key: "netPay",
      header: "Net",
      align: "right",
      render: (r) => (
        <span className="font-bold text-success-700">₹{Number(r.netPay || 0).toLocaleString()}</span>
      ),
    },
    {
      key: "paymentStatus",
      header: "Payment",
      nowrap: true,
      render: (r) => (
        <Badge variant={r.paymentStatus === "PAID" ? "success" : "warning"}>
          {r.paymentStatus || "PENDING"}
        </Badge>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
      {/* Top Header */}
      <div className="flex flex-col gap-4 border-b border-neutral-200 dark:border-neutral-800 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to={`${base}/${runId}`}
            className="mb-1 inline-flex items-center gap-1 text-sm font-semibold text-neutral-500 dark:text-neutral-400 transition-colors hover:text-primary-600"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Back to Run
          </Link>
          <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
            Payroll Register
          </h1>
          <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
            {run.month}/{run.year} · Staff:{" "}
            <span className="font-semibold text-neutral-800 dark:text-neutral-100">{run.totalStaffCount}</span> · Net:{" "}
            <span className="font-bold text-success-700">
              ₹{Number(run.totalNetPay || 0).toLocaleString()}
            </span>
          </p>
        </div>
      </div>

      {/* Main Register Table */}
      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            density="compact"
            emptyState={
              <EmptyState icon={FileSpreadsheet} title="No payroll register records found" />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}